import store from "../store/store";
import { logout } from "../store/auth";

const LOCALE_STORAGE_KEY = "cyberpass_locale";

export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

/**
 * Wrapper around fetch that handles 401 responses by logging out the user.
 * Automatically adds Accept-Language header based on saved locale preference.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 * @throws {SessionExpiredError} When the session has expired (401 response)
 */
export async function apiFetch(url, options = {}) {
  const accessToken = store.getState().auth.accessToken;
  if (accessToken && !options.headers?.["Authorization"]) {
    options.headers = {
      ...options.headers,
      Authorization: `${accessToken.tokenType} ${accessToken.accessToken}`,
    };
  }

  // Set Accept-Language from saved locale preference
  if (!options.headers?.["Accept-Language"]) {
    const savedLocale = readSavedLocale();
    options.headers = {
      ...options.headers,
      "Accept-Language": savedLocale,
    };
  }

  const resp = await fetch(url, options);

  if (resp.status === 401) {
    store.dispatch(logout());
    throw new SessionExpiredError();
  }

  return resp;
}

/**
 * @returns {"ru" | "en"}
 */
function readSavedLocale() {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === "ru" || saved === "en") return saved;
  } catch {
    // localStorage may not be available
  }
  return "ru";
}

/**
 * Creates fetch options with Authorization header
 * @param {AccessToken} accessToken
 * @param {RequestInit} [fetchConfig]
 * @returns {RequestInit}
 */
export function withAuth(accessToken, fetchConfig) {
  if (fetchConfig == null) {
    fetchConfig = {};
  }

  if (fetchConfig.headers == null) {
    fetchConfig.headers = {};
  }

  fetchConfig.headers["Authorization"] =
    `${accessToken.tokenType} ${accessToken.accessToken}`;

  if (!fetchConfig.headers["Accept-Language"]) {
    fetchConfig.headers["Accept-Language"] = readSavedLocale();
  }

  return fetchConfig;
}
